import React, { type MouseEvent } from 'react';
import camelCase from 'lodash/camelCase';
import Link from '@atlaskit/link';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

type Props = {
	articleId: string;
	linkText: MessageDescriptor;
	documentationUrl?: string;
	analyticsOptions?: {
		event: {
			action: string;
			actionSubject: string;
		};
		actionSubjectId: string;
	};
};

export const IPHLink = (props: Props) => {
	const { articleId, linkText, analyticsOptions, documentationUrl } = props;

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onOpenInProductHelpArticle = useHelpPanelLink();

	const handleClick =
		(clickType: 'primary' | 'aux' | 'context') => (e: MouseEvent<HTMLElement>) => {
			const modifiersPressed = e && Boolean(e.shiftKey || e.metaKey || e.ctrlKey);
			const shouldOpenLinkInIph =
				// on primary button click
				clickType === 'primary' &&
				// no modifier buttons have been pressed
				!modifiersPressed;

			// if documentationUrl is missing, fallback to IPH always
			if (shouldOpenLinkInIph || !documentationUrl) {
				onOpenInProductHelpArticle(articleId);
				// prevent the link from opening as usual
				e.preventDefault();
			}

			const actionSubjectId = camelCase(linkText.defaultMessage);

			const analyticsAttributes = {
				clickType,
				actionSubjectId,
				articleId,
				modifiersPressed,
			};

			if (analyticsOptions) {
				const event = createAnalyticsEvent(analyticsOptions.event);
				fireUIAnalytics(event, analyticsOptions.actionSubjectId, {
					...analyticsAttributes,
					actionSubjectId,
				});
			} else {
				const event = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'IPH link',
				});

				const uiAnalytics = 'helpLink clicked';
				fireUIAnalytics(event, uiAnalytics, actionSubjectId, analyticsAttributes);
			}
		};

	return (
		<Link
			href={documentationUrl ?? ''}
			target="_blank"
			onContextMenu={handleClick('context')}
			onClick={handleClick('primary')}
			onAuxClick={handleClick('aux')}
		>
			{formatMessage(linkText)}
		</Link>
	);
};
